import React, { useState } from "react";
import { ScopeSelect } from "./ScopeSelect";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "./ui/sonner";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { Plus, Receipt } from "lucide-react";

/** 浏览器本地日期 YYYY-MM-DD：费用记的是「市场部说的那天」，不能用 UTC 换算（洛杉矶下午会差一天）。 */
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function SourceManager({ open, onClose, api, zh, regions, initialRegion, lockRegion, onChanged, canEditSettings = false }) {
  const [region, setRegion] = useState("");
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [cost, setCost] = useState(30);
  const [spend, setSpend] = useState(0);
  const [busy, setBusy] = useState(false);
  const [histFor, setHistFor] = useState(null);
  const [histList, setHistList] = useState([]);
  const [roiData, setRoiData] = useState([]);
  const [hMonth, setHMonth] = useState(new Date().toISOString().slice(0, 7));
  const [hSpend, setHSpend] = useState(0);
  const [roiTarget, setRoiTarget] = useState(0);
  // 活动/费用明细（2026-10-22 新增）：一条 = 一次投放，活动名 + 发生日期 + 金额
  const [expList, setExpList] = useState([]);
  const [expTotal, setExpTotal] = useState(0);
  const [eTitle, setETitle] = useState("");
  const [eDate, setEDate] = useState(todayLocal);
  const [eAmount, setEAmount] = useState("");
  const [eBusy, setEBusy] = useState(false);
  React.useEffect(() => { if (open) api.get("/crm/settings").then(({ data }) => setRoiTarget(data?.roiTarget || 0)).catch(() => {}); }, [open]); // eslint-disable-line
  /**
   * ROI 预警线是**全公司唯一一条**设置（`crm_settings` 里只有一个 `key: "crm"` 文档，
   * 没有分公司维度），后端 `/crm/settings` PUT 只允许 admin / ceo。
   * 【2026-10-22 修线上反馈】原来这个输入框无条件绑了 `onBlur → PUT`：
   * 分公司市场负责人（如圣地亚哥 Yvonne）打开来源管理、随手关掉，输入框失焦就发一次
   * 她根本无权做的 PUT → 403，前端弹「保存失败」，控制台一直刷红。
   * 现在两道闸：① 值没变化不发请求（关弹窗不该产生写操作）；
   * ② 没写权限的人根本不渲染可编辑输入框，只显示当前值。
   */
  const saveRoiTarget = async (v) => {
    if (!canEditSettings) return;
    const next = Number(v) || 0;
    if (next === Number(roiTarget || 0)) return;
    try { await api.put("/crm/settings", { roiTarget: next }); setRoiTarget(next); toast.success(zh ? "已保存 ROI 预警线" : "Saved"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed")); }
  };
  React.useEffect(() => { if (!open) return; if (lockRegion) setRegion(lockRegion); else if (initialRegion) setRegion(initialRegion); else if (regions?.length && !region) setRegion(regions[0].id); }, [open, initialRegion, lockRegion, regions]); // eslint-disable-line
  const load = React.useCallback(() => { if (!region) return; api.get(`/crm/sources?region=${region}`).then(({ data }) => setList(data || [])).catch(() => {}); }, [api, region]);
  React.useEffect(() => { if (open) load(); }, [open, load]);
  const add = async () => {
    if (!name.trim()) { toast.error(zh ? "请输入来源名称" : "Name required"); return; }
    setBusy(true);
    try { await api.post("/crm/sources", { region, name: name.trim(), adCost: Number(cost) || 0, monthlySpend: Number(spend) || 0 }); setName(""); setCost(30); setSpend(0); toast.success(zh ? "已添加" : "Added"); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "添加失败" : "Failed")); }
    setBusy(false);
  };
  const del = async (id) => { try { await api.delete(`/crm/sources/${id}`); load(); onChanged?.(); } catch { toast.error(zh ? "删除失败" : "Failed"); } };
  // 单次成本 / 月花费同理：值没变就不发 PATCH —— 关弹窗、点别处都会触发 blur，
  // 无意义的写请求除了白刷 audit 日志，还会在无权限时把用户吓一跳。
  const saveField = async (id, field, v, prev) => {
    const next = Number(v) || 0;
    if (next === Number(prev || 0)) return;
    try { await api.patch(`/crm/sources/${id}`, { [field]: next }); onChanged?.(); } catch { toast.error(zh ? "保存失败" : "Failed"); }
  };
  const saveName = async (id, v, prev) => {
    const nm = (v || "").trim();
    if (!nm || nm === prev) return;
    try { await api.patch(`/crm/sources/${id}`, { name: nm }); toast.success(zh ? "来源已重命名，历史客户来源已同步" : "Renamed & leads synced"); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "重命名失败" : "Rename failed")); load(); }
  };

  const loadExp = async (s) => {
    const qs = `region=${s.region}&sourceName=${encodeURIComponent(s.name)}`;
    try { const { data } = await api.get(`/crm/sources/expenses?${qs}`); setExpList(data?.items || []); setExpTotal(data?.total || 0); }
    catch { setExpList([]); setExpTotal(0); }
  };
  const openHist = async (s) => {
    setHistFor(s); setHSpend(0); setHMonth(new Date().toISOString().slice(0, 7));
    setETitle(""); setEAmount(""); setEDate(todayLocal());
    const qs = `region=${s.region}&sourceName=${encodeURIComponent(s.name)}`;
    loadExp(s);
    try { const { data } = await api.get(`/crm/sources/spend-history?${qs}`); setHistList(data || []); } catch { setHistList([]); }
    try { const { data } = await api.get(`/crm/sources/roi-history?${qs}&months=6`); setRoiData(data?.data || []); } catch { setRoiData([]); }
  };
  const refreshHist = async (s) => {
    const qs = `region=${s.region}&sourceName=${encodeURIComponent(s.name)}`;
    try { const { data } = await api.get(`/crm/sources/spend-history?${qs}`); setHistList(data || []); } catch {}
    try { const { data } = await api.get(`/crm/sources/roi-history?${qs}&months=6`); setRoiData(data?.data || []); } catch {}
  };
  const saveHist = async () => {
    if (!histFor || hMonth.length !== 7) { toast.error(zh ? "请填月份 YYYY-MM" : "Month required"); return; }
    try {
      await api.post("/crm/sources/spend", { region: histFor.region, sourceName: histFor.name, month: hMonth, spend: Number(hSpend) || 0 });
      await refreshHist(histFor); setHSpend(0); toast.success(zh ? "已保存" : "Saved"); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed")); }
  };
  const addExp = async () => {
    if (!histFor) return;
    const amt = Number(eAmount);
    if (!eTitle.trim()) { toast.error(zh ? "请填活动 / 项目名称" : "Activity name required"); return; }
    if (!(amt > 0)) { toast.error(zh ? "请填大于 0 的金额" : "Amount must be > 0"); return; }
    setEBusy(true);
    try {
      await api.post("/crm/sources/expenses", { region: histFor.region, sourceName: histFor.name, title: eTitle.trim(), date: eDate, amount: amt });
      setETitle(""); setEAmount("");
      await loadExp(histFor); await refreshHist(histFor);
      toast.success(zh ? "已记录这笔费用" : "Expense added"); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed")); }
    setEBusy(false);
  };
  const voidExp = async (id) => {
    try { await api.delete(`/crm/sources/expenses/${id}`); await loadExp(histFor); await refreshHist(histFor); toast.success(zh ? "已作废" : "Voided"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "作废失败" : "Failed")); }
  };
  const sel = "text-sm rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 focus:border-indigo-400 focus:outline-none";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="source-manager-dialog">
        <DialogHeader>
          <DialogTitle>{zh ? "营销来源类别管理" : "Marketing Sources"}</DialogTitle>
          <DialogDescription>{zh ? "每家公司独立维护来源。点「费用明细」逐笔登记活动/广告花费（可跨月、可先花钱后进客户）；留空的月花费按「单次获客成本 × Leads」估算。" : "Each company maintains its own sources. Use Expenses to record each campaign spend; blank monthly spend falls back to per-lead cost."}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-slate-500">{zh ? "公司/区域" : "Company"}</span>
          {lockRegion ? (
            <span data-testid="source-mgr-region-locked" className="text-sm font-medium text-slate-700 bg-slate-100 rounded-lg px-2.5 py-1.5">{(() => { const r = (regions || []).find((x) => x.id === lockRegion); return r ? (zh ? r.zh : r.en) : lockRegion; })()}</span>
          ) : (
            <ScopeSelect kind="region" data-testid="source-mgr-region" className={sel} value={region} onChange={(e) => setRegion(e.target.value)}>
              {(regions || []).map((r) => <option key={r.id} value={r.id}>{zh ? r.zh : r.en}</option>)}
            </ScopeSelect>
          )}
          <span className="text-xs text-slate-500 ml-auto">{zh ? "ROI 预警线%" : "ROI alert%"}</span>
          {/* key 跟随已保存值：GET /crm/settings 是打开后异步回来的，defaultValue 只认首次挂载，
              不加 key 就会一直显示上一次的值（第一次是 0）。 */}
          {canEditSettings ? (
            <Input key={roiTarget} data-testid="roi-target-input" type="number" defaultValue={roiTarget} onBlur={(e) => saveRoiTarget(e.target.value)} className="w-20 h-8 text-sm" title={zh ? "营销表 ROI 低于此值标红" : "sources below turn red"} />
          ) : (
            <span data-testid="roi-target-readonly" title={zh ? "ROI 预警线是全公司统一设置，由总部（管理员）维护；本分公司只读" : "Company-wide setting, maintained by HQ (read-only)"} className="w-20 h-8 inline-flex items-center justify-end rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm text-slate-600">{roiTarget || 0}</span>
          )}
        </div>
        <div className="grid grid-cols-[1fr_88px_110px_auto] gap-2 items-center text-[11px] text-slate-400 px-1">
          <span>{zh ? "来源" : "Source"}</span><span className="text-right">{zh ? "单次成本$" : "$/lead"}</span><span className="text-right">{zh ? "月花费$" : "Monthly$"}</span><span></span>
        </div>
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {list.map((s) => (
            <div key={s.id} className="grid grid-cols-[1fr_88px_110px_auto] gap-2 items-center border border-slate-100 rounded-lg px-3 py-2" data-testid={`source-item-${s.id}`}>
              <Input data-testid={`source-name-${s.id}`} defaultValue={s.name} onBlur={(e) => saveName(s.id, e.target.value, s.name)} className="h-8 text-sm" title={zh ? "点此重命名来源（历史客户来源会同步更新）" : "rename source (leads sync)"} />
              <Input data-testid={`source-cost-${s.id}`} type="number" defaultValue={s.adCost} onBlur={(e) => saveField(s.id, "adCost", e.target.value, s.adCost)} className="h-8 text-sm text-right" title={zh ? "单次获客成本" : "cost per lead"} />
              <Input data-testid={`source-spend-${s.id}`} type="number" defaultValue={s.monthlySpend || 0} onBlur={(e) => saveField(s.id, "monthlySpend", e.target.value, s.monthlySpend)} className="h-8 text-sm text-right" title={zh ? "本月真实广告花费" : "actual monthly spend"} />
              <div className="flex items-center gap-1">
                <button data-testid={`source-hist-${s.id}`} onClick={() => openHist(s)} className="text-indigo-400 hover:text-indigo-600 text-xs px-1" title={zh ? "逐笔登记活动 / 广告费用" : "Record campaign expenses"}>{zh ? "费用明细" : "Expenses"}</button>
                <button data-testid={`source-del-${s.id}`} onClick={() => del(s.id)} className="text-red-400 hover:text-red-600 text-xs px-1">{zh ? "删除" : "Del"}</button>
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-sm text-slate-300 text-center py-4">{zh ? "该公司暂无来源，请添加。" : "No sources."}</p>}
        </div>
        <div className="flex items-end gap-2 border-t border-slate-100 pt-3">
          <div className="flex-1"><label className="text-xs text-slate-500">{zh ? "新来源名称" : "New source"}</label><Input data-testid="source-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={zh ? "如 TikTok / 抖音 / 某场展会" : "e.g. TikTok"} className="h-9" /></div>
          <div className="w-20"><label className="text-xs text-slate-500">{zh ? "成本$" : "Cost$"}</label><Input data-testid="source-new-cost" type="number" value={cost} onChange={(e) => setCost(e.target.value)} className="h-9" /></div>
          <div className="w-24"><label className="text-xs text-slate-500">{zh ? "月花费$" : "Monthly$"}</label><Input data-testid="source-new-spend" type="number" value={spend} onChange={(e) => setSpend(e.target.value)} className="h-9" /></div>
          <Button data-testid="source-add-btn" onClick={add} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9"><Plus className="h-4 w-4 mr-1" />{zh ? "添加" : "Add"}</Button>
        </div>
        <Dialog open={!!histFor} onOpenChange={(v) => !v && setHistFor(null)}>
          <DialogContent className="max-w-lg" data-testid="source-hist-dialog">
            <DialogHeader>
              <DialogTitle>{zh ? "活动 / 费用明细" : "Campaign Expenses"} · {histFor?.name}</DialogTitle>
              <DialogDescription>{zh ? "一笔一条：哪场活动、哪天花的、多少钱。活动还没开始或还没带来客户也可以先记，报表会照实显示花费。" : "One row per campaign: what, when, how much. Spend is counted even before leads arrive."}</DialogDescription>
            </DialogHeader>
            {roiData.length > 0 && (
              <div data-testid="source-roi-chart" className="mb-2">
                <ResponsiveContainer width="100%" height={150}>
                  <ComposedChart data={roiData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v, n) => n === "roi" ? `${v}%` : `$${Number(v).toLocaleString()}`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="spend" name={zh ? "广告花费" : "Spend"} fill="#f59e0b" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="revenue" name={zh ? "签约额" : "Revenue"} fill="#16a34a" radius={[3, 3, 0, 0]} />
                    <Line type="monotone" dataKey="roi" name="ROI%" stroke="#7B5CFF" strokeWidth={2} yAxisId={0} dot={{ r: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5" data-testid="source-expense-form">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Receipt className="h-3.5 w-3.5 text-indigo-500" />{zh ? "记一笔花费" : "Add expense"}</span>
                <span className="text-[11px] text-slate-500">{zh ? "未作废合计" : "Total"} <b className="text-slate-800">${expTotal.toLocaleString()}</b></span>
              </div>
              <div className="flex items-end gap-1.5">
                <div className="w-32"><label className="text-[10px] text-slate-500">{zh ? "日期" : "Date"}</label><Input data-testid="exp-date" type="date" value={eDate} onChange={(e) => setEDate(e.target.value)} className="h-8 text-xs" /></div>
                <div className="flex-1"><label className="text-[10px] text-slate-500">{zh ? "活动 / 项目" : "Campaign"}</label><Input data-testid="exp-title" value={eTitle} onChange={(e) => setETitle(e.target.value)} placeholder={zh ? "如 Carlsbad Village Fair" : "e.g. Carlsbad Village Fair"} className="h-8 text-xs" /></div>
                <div className="w-24"><label className="text-[10px] text-slate-500">{zh ? "金额$" : "Amount$"}</label><Input data-testid="exp-amount" type="number" value={eAmount} onChange={(e) => setEAmount(e.target.value)} className="h-8 text-xs text-right" /></div>
                <Button data-testid="exp-add-btn" onClick={addExp} disabled={eBusy} className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 px-2.5 text-xs"><Plus className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
            <div className="space-y-1 max-h-44 overflow-y-auto mt-1.5" data-testid="source-expense-list">
              {expList.map((x) => (
                <div key={x.id} className={`flex items-center gap-2 text-xs border-b border-slate-50 py-1.5 ${x.voidedAt ? "opacity-40" : ""}`} data-testid={`exp-row-${x.id}`} data-voided={x.voidedAt ? 1 : 0}>
                  <span className="text-slate-400 w-[74px] shrink-0">{x.date}</span>
                  <span className={`flex-1 truncate ${x.voidedAt ? "line-through text-slate-400" : "text-slate-700"}`} title={x.note || x.title}>{x.title}</span>
                  <span className={`font-semibold shrink-0 ${x.voidedAt ? "text-slate-400 line-through" : "text-slate-800"}`}>${Number(x.amount || 0).toLocaleString()}</span>
                  {x.voidedAt
                    ? <span className="text-[10px] text-slate-400 shrink-0 w-8 text-right">{zh ? "已作废" : "void"}</span>
                    : <button data-testid={`exp-void-${x.id}`} onClick={() => voidExp(x.id)} className="text-red-300 hover:text-red-600 shrink-0 w-8 text-right" title={zh ? "作废这笔（保留痕迹，不计入统计）" : "Void"}>{zh ? "作废" : "Void"}</button>}
                </div>
              ))}
              {expList.length === 0 && <p className="text-xs text-slate-300 text-center py-3">{zh ? "还没有逐笔记录。上面的「月花费$」仍可整月录入。" : "No expenses yet."}</p>}
            </div>
            <details className="border-t border-slate-100 pt-2" data-testid="source-monthly-spend">
              <summary className="text-[11px] text-slate-500 cursor-pointer select-none">{zh ? "按月补录总额（旧口径，明细优先）" : "Monthly totals (legacy)"}</summary>
              <div className="space-y-1 max-h-32 overflow-y-auto mt-1.5">
                {histList.map((h) => (
                  <div key={h.month} className="flex items-center justify-between text-xs border-b border-slate-50 py-1" data-testid={`hist-row-${h.month}`}>
                    <span className="text-slate-600">{h.month}</span><span className="font-semibold text-slate-800">${h.spend.toLocaleString()}</span>
                  </div>
                ))}
                {histList.length === 0 && <p className="text-xs text-slate-300 text-center py-2">{zh ? "暂无记录" : "No history"}</p>}
              </div>
              <div className="flex items-end gap-2 mt-2">
                <div className="flex-1"><label className="text-[10px] text-slate-500">{zh ? "月份" : "Month"}</label><Input data-testid="hist-month" value={hMonth} onChange={(e) => setHMonth(e.target.value)} placeholder="YYYY-MM" className="h-8 text-xs" /></div>
                <div className="w-24"><label className="text-[10px] text-slate-500">{zh ? "花费$" : "Spend$"}</label><Input data-testid="hist-spend" type="number" value={hSpend} onChange={(e) => setHSpend(e.target.value)} className="h-8 text-xs text-right" /></div>
                <Button data-testid="hist-save" onClick={saveHist} className="bg-slate-700 hover:bg-slate-800 text-white h-8 px-3 text-xs">{zh ? "保存" : "Save"}</Button>
              </div>
            </details>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
